import { useState } from "react";
import { ChevronDown } from "lucide-react";

import { Label } from "@/components/ui/label";
import { SizeInput } from "@/components/ui/size-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import PropertySection from "../PropertySection";
import {
  SIZE_UNITS_WIDTH,
  SIZE_UNITS_HEIGHT,
  SIZE_UNITS_MAX,
} from "@/constants/property-panel.constants";

interface LayoutSectionProps {
  styles: Record<string, string>;
  onStyleChange: (property: string, value: string) => void;
}

const DISPLAY_OPTIONS = [
  "block",
  "flex",
  "grid",
  "inline",
  "inline-flex",
  "inline-block",
  "none",
];

const DIRECTION_OPTIONS = [
  { value: "row", label: "Row →" },
  { value: "row-reverse", label: "Row Reverse ←" },
  { value: "column", label: "Column ↓" },
  { value: "column-reverse", label: "Column Reverse ↑" },
];

const JUSTIFY_OPTIONS = [
  { value: "flex-start", label: "Start" },
  { value: "center", label: "Center" },
  { value: "flex-end", label: "End" },
  { value: "space-between", label: "Between" },
  { value: "space-around", label: "Around" },
  { value: "space-evenly", label: "Evenly" },
];

const ALIGN_OPTIONS = [
  { value: "stretch", label: "Stretch" },
  { value: "flex-start", label: "Start" },
  { value: "center", label: "Center" },
  { value: "flex-end", label: "End" },
  { value: "baseline", label: "Baseline" },
];

const FLEX_WRAP_OPTIONS = [
  { value: "nowrap", label: "No Wrap" },
  { value: "wrap", label: "Wrap" },
  { value: "wrap-reverse", label: "Wrap Reverse" },
];

const GRID_COLS_OPTIONS = [
  { value: "", label: "Custom" },
  { value: "repeat(1, minmax(0, 1fr))", label: "1 Column" },
  { value: "repeat(2, minmax(0, 1fr))", label: "2 Columns" },
  { value: "repeat(3, minmax(0, 1fr))", label: "3 Columns" },
  { value: "repeat(4, minmax(0, 1fr))", label: "4 Columns" },
  { value: "repeat(5, minmax(0, 1fr))", label: "5 Columns" },
  { value: "repeat(6, minmax(0, 1fr))", label: "6 Columns" },
];

/** Normalize computed grid-template-columns to match option values.
 *  getComputedStyle returns resolved pixel values like "198px 198px 198px",
 *  not the authored "repeat(3, minmax(0, 1fr))".
 *  We detect equal-width columns and map to our repeat() options. */
function normalizeGridCols(value: string): string {
  if (!value) return "";

  // Already in repeat() form (e.g. from our own onChange)
  if (value.startsWith("repeat(")) {
    return value.replace(/minmax\(0px,/g, "minmax(0,");
  }

  // Already in minmax() expanded form
  if (value.includes("minmax(")) {
    const parts = value.trim().split(/\s+/);
    const normalized = parts[0]?.replace(/0px/g, "0") || "";
    if (
      parts.length > 0 &&
      parts.every((p) => p.replace(/0px/g, "0") === normalized)
    ) {
      return `repeat(${parts.length}, ${normalized})`;
    }
    return value;
  }

  // Resolved pixel values like "198px 198px 198px"
  // Count equal-width columns and map to repeat(N, minmax(0, 1fr))
  const parts = value.trim().split(/\s+/);
  if (parts.length >= 1 && parts.length <= 6) {
    // Check if all columns are roughly equal width (within 1px tolerance for rounding)
    const widths = parts.map((p) => parseFloat(p));
    const allEqual = widths.every(
      (w) => !isNaN(w) && Math.abs(w - widths[0]) < 1,
    );
    if (allEqual) {
      return `repeat(${parts.length}, minmax(0, 1fr))`;
    }
  }

  return "";
}

/** Compact select with consistent styling */
function CompactSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full h-8 bg-background border-border text-foreground text-xs">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-card border-border">
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-xs">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export const LayoutSection = ({
  styles,
  onStyleChange,
}: LayoutSectionProps): React.JSX.Element => {
  const [showMinMax, setShowMinMax] = useState(false);
  const display = styles.display || "";
  const isFlex = display === "flex" || display === "inline-flex";
  const isGrid = display === "grid";

  return (
    <PropertySection title="Layout" defaultOpen>
      <div className="flex flex-col gap-3">
        {/* Display */}
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">Display</Label>
          <Select
            value={display || "default"}
            onValueChange={(v) =>
              onStyleChange("display", v === "default" ? "" : v)
            }
          >
            <SelectTrigger className="w-full h-8 bg-background border-border text-foreground text-xs">
              <SelectValue placeholder="Default" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="default" className="text-xs">
                Default
              </SelectItem>
              {DISPLAY_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt} className="text-xs">
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Flex Options */}
        {isFlex && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <CompactSelect
                label="Direction"
                value={styles.flexDirection || "row"}
                onChange={(v) => onStyleChange("flexDirection", v)}
                options={DIRECTION_OPTIONS}
              />
              <CompactSelect
                label="Wrap"
                value={styles.flexWrap || "nowrap"}
                onChange={(v) => onStyleChange("flexWrap", v)}
                options={FLEX_WRAP_OPTIONS}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <CompactSelect
                label="Justify"
                value={styles.justifyContent || "flex-start"}
                onChange={(v) => onStyleChange("justifyContent", v)}
                options={JUSTIFY_OPTIONS}
              />
              <CompactSelect
                label="Align"
                value={styles.alignItems || "stretch"}
                onChange={(v) => onStyleChange("alignItems", v)}
                options={ALIGN_OPTIONS}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Gap</Label>
              <SizeInput
                value={styles.gap}
                onChange={(value) => onStyleChange("gap", value)}
                placeholder="0"
                units={["px", "rem", "em", "%"]}
                defaultUnit="px"
              />
            </div>
          </>
        )}

        {/* Grid Options */}
        {isGrid && (
          <>
            <CompactSelect
              label="Columns"
              value={normalizeGridCols(styles.gridTemplateColumns || "")}
              onChange={(v) =>
                onStyleChange("gridTemplateColumns", v === "custom" ? "" : v)
              }
              options={GRID_COLS_OPTIONS.map((opt) => ({
                value: opt.value || "custom",
                label: opt.label,
              }))}
              placeholder="Custom"
            />
            <div className="grid grid-cols-2 gap-2">
              <CompactSelect
                label="Justify"
                value={styles.justifyContent || "flex-start"}
                onChange={(v) => onStyleChange("justifyContent", v)}
                options={JUSTIFY_OPTIONS}
              />
              <CompactSelect
                label="Align"
                value={styles.alignItems || "stretch"}
                onChange={(v) => onStyleChange("alignItems", v)}
                options={ALIGN_OPTIONS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Gap</Label>
              <SizeInput
                value={styles.gap}
                onChange={(value) => onStyleChange("gap", value)}
                placeholder="0"
                units={["px", "rem", "em", "%"]}
                defaultUnit="px"
              />
            </div>
          </>
        )}

        {/* Width & Height */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Width</Label>
            <SizeInput
              value={styles.width}
              onChange={(value) => onStyleChange("width", value)}
              placeholder="auto"
              units={SIZE_UNITS_WIDTH}
              defaultUnit="px"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Height</Label>
            <SizeInput
              value={styles.height}
              onChange={(value) => onStyleChange("height", value)}
              placeholder="auto"
              units={SIZE_UNITS_HEIGHT}
              defaultUnit="px"
            />
          </div>
        </div>

        {/* Min/Max Toggle */}
        <button
          type="button"
          onClick={() => setShowMinMax(!showMinMax)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer self-start"
        >
          <ChevronDown
            className={cn(
              "h-3 w-3 transition-transform",
              showMinMax && "rotate-180",
            )}
          />
          Min / Max
        </button>

        {showMinMax && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Min Width
                </Label>
                <SizeInput
                  value={styles.minWidth}
                  onChange={(value) => onStyleChange("minWidth", value)}
                  placeholder="auto"
                  units={SIZE_UNITS_WIDTH}
                  defaultUnit="px"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Min Height
                </Label>
                <SizeInput
                  value={styles.minHeight}
                  onChange={(value) => onStyleChange("minHeight", value)}
                  placeholder="auto"
                  units={SIZE_UNITS_HEIGHT}
                  defaultUnit="px"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Max Width
                </Label>
                <SizeInput
                  value={styles.maxWidth}
                  onChange={(value) => onStyleChange("maxWidth", value)}
                  placeholder="none"
                  units={SIZE_UNITS_MAX}
                  defaultUnit="px"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Max Height
                </Label>
                <SizeInput
                  value={styles.maxHeight}
                  onChange={(value) => onStyleChange("maxHeight", value)}
                  placeholder="none"
                  units={SIZE_UNITS_MAX.map((u) => (u === "vw" ? "vh" : u))}
                  defaultUnit="px"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </PropertySection>
  );
};
